import React, { useState } from 'react';
import { PortalSetupModal } from './PortalSetupModal';
import { PortalConfigModal } from './PortalConfigModal';
import { Customer } from '@/hooks/useCustomers';
import { useSourceManager } from '@/hooks/useSourceManager';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface PortalSourceCardProps {
  customer: Customer;
  onSetupMode?: (mode: string) => void;
}

interface ConnectedPortal {
  id: string;
  region: string;
  supplier: string;
  logo: string;
  status: string;
  lastSync: string;
  billsCollected: number;
  apiConfig?: {
    retailer_lookup_endpoint: string;
    bot_trigger_endpoint: string;
    auth_method: 'bearer' | 'api_key' | 'basic' | 'custom';
    auth_config: Record<string, any>;
    webhook_url?: string;
    timeout?: number;
  };
}

export const PortalSourceCard: React.FC<PortalSourceCardProps> = ({ customer }) => {
  const { sources, enableSource, disableSource, testSource, loading } = useSourceManager(customer.id);
  const [showSetup, setShowSetup] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [selectedPortal, setSelectedPortal] = useState<ConnectedPortal | null>(null);
  const [connectedPortals, setConnectedPortals] = useState<ConnectedPortal[]>([
    // Example connected portal - in production this would come from database
    {
      id: '1',
      region: 'USA',
      supplier: 'Pacific Gas & Electric (PG&E)',
      logo: '/logos/pge.png',
      status: 'active',
      lastSync: '6 hours ago',
      billsCollected: 8
    }
  ]);

  const portalSource = sources.portal;
  const isActive = portalSource?.status === 'active';

  const handleToggle = async () => {
    if (isActive) {
      await disableSource('portal');
    } else {
      await enableSource('portal', { auto_enabled: true });
    }
  };

  const handleTest = async () => {
    await testSource('portal');
  };

  const handleSyncAll = async () => {
    // In production, this would trigger sync for all connected portals
    console.log('Syncing all portals...');
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <span className="text-xl">🏢</span>
          </div>
          <div>
            <h3 className="font-medium text-foreground">Portal Integration</h3>
            <p className="text-sm text-muted-foreground">Connect to utility company portals</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={isActive ? "default" : "secondary"}>
            {isActive ? "✅ Active" : "⏸️ Inactive"}
          </Badge>
          <span className="px-2 py-1 rounded text-xs font-medium bg-primary/10 text-primary">
            {connectedPortals.length} connected
          </span>
          <Button
            variant={isActive ? "outline" : "default"}
            size="sm"
            onClick={handleToggle}
            disabled={loading}
          >
            {isActive ? "Disable" : "Enable"}
          </Button>
          <Button 
            size="sm"
            onClick={() => setShowSetup(true)}
          >
            + Add Portal
          </Button>
        </div>
      </div>

      {/* Connected Portals */}
      {connectedPortals.length > 0 && (
        <div className="mb-4">
          <div className="text-xs font-medium text-foreground mb-2">Connected Portals</div>
          <div className="space-y-2">
            {connectedPortals.map(portal => (
              <div key={portal.id} className="flex items-center justify-between p-3 bg-primary/5 rounded border border-primary/20">
                <div className="flex items-center gap-3">
                  <img 
                    src={portal.logo} 
                    alt={portal.supplier}
                    className="w-8 h-8 rounded"
                    onError={(e) => {(e.target as HTMLImageElement).src = '/placeholder-logo.png'}}
                  />
                  <div>
                    <div className="text-sm font-medium text-foreground">{portal.supplier}</div>
                    <div className="text-xs text-muted-foreground">{portal.region} • Last sync: {portal.lastSync}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-green-500/10 text-green-600 px-2 py-1 rounded">
                    ✅ Active
                  </span>
                  <span className="text-xs text-muted-foreground">{portal.billsCollected} bills</span>
                  <button 
                    className="text-primary text-xs hover:underline"
                    onClick={() => {
                      setSelectedPortal(portal);
                      setShowConfig(true);
                    }}
                  >
                    Configure
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Portal Benefits */}
      <div className="p-3 bg-muted rounded mb-4">
        <div className="text-xs font-medium text-foreground mb-2">🌟 Portal Integration Benefits</div>
        <ul className="text-xs text-muted-foreground space-y-1">
          <li>• 🔄 Automatic bill collection from utility websites</li>
          <li>• 📅 Scheduled downloads (daily/weekly/monthly)</li>
          <li>• 🔐 Secure credential storage with encryption</li>
          <li>• 🌍 Support for USA, Australia, UK suppliers</li>
          <li>• 📊 Real-time sync status monitoring</li>
        </ul>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <Button onClick={() => setShowSetup(true)}>
          🏢 Add Portal
        </Button>
        <Button variant="outline" onClick={handleSyncAll} disabled={loading}>
          🔄 Sync All
        </Button>
        <Button variant="outline" onClick={handleTest} disabled={loading}>
          🧪 Test Connection
        </Button>
      </div>

      {/* Portal Setup Modal */}
      {showSetup && (
        <PortalSetupModal
          customer={customer}
          onClose={() => setShowSetup(false)}
          onPortalAdded={(portal) => {
            setConnectedPortals([...connectedPortals, portal]);
            setShowSetup(false);
          }}
        />
      )}

      {/* Portal Config Modal */}
      {showConfig && selectedPortal && (
        <PortalConfigModal
          portal={selectedPortal}
          onClose={() => {
            setShowConfig(false);
            setSelectedPortal(null);
          }}
          onConfigSaved={(updatedPortal) => {
            setConnectedPortals(prev => 
              prev.map(p => p.id === updatedPortal.id ? updatedPortal : p)
            );
            setShowConfig(false);
            setSelectedPortal(null);
          }}
        />
      )}
    </div>
  );
};